(ns app.front.ui.sub-header
  "Global sub-header component"
  (:require ["@carbon/icons-react/es/chat/24" :default Chat24]
            ["@carbon/icons-react/es/chevron--left/24" :default ChevronLeft24]
            ["@carbon/icons-react/es/close/24" :default Close24]
            ["@carbon/icons-react/es/information/24" :default Information24]
            ["@carbon/icons-react/es/menu/24" :default Menu24]
            ["@carbon/icons-react/es/open-panel--left/24" :default OpenPanelLeft24]
            ["@carbon/icons-react/es/task/24" :default Task24]
            [app.front.ui :as ui]
            [app.front.ui.call-to-action :as call-to-action]
            [app.front.ui.chat-panel :as chat-panel]
            [app.front.ui.messages-panel :as messages-panel]
            [app.front.ui.options-panel :as options-panel]
            [app.front.ui.panels :as panels]
            [app.front.ui.service-hub-panel :as service-hub-panel]
            [app.front.ui.tasks-panel :as tasks-panel]
            [app.front.ui.top-bar :as top-bar]
            [app.front.ui.visual-viewport :as visual-viewport]
            [app.front.util :refer [for-frag]]
            [app.front.view.landing-page-subs :as landing-page-subs]
            [app.front.view.navigation :as nav]
            [app.front.view.workspace.primary-actions :as primary-actions]
            [core.front.carbon :as cc]
            [core.front.carbon-icons :as carbon-icons]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))


(def menu-item-icon-mapping {:navigation/service-hub OpenPanelLeft24
                             :navigation/messages    Chat24
                             :navigation/tasks       Task24
                             :navigation/options     Menu24})

(rf/reg-sub ::default-view-data
  :<- [::href/branded-href :dashboard]
  (fn [back-href _]
    {:back-href back-href}))


(defn- notification-indicator []
  [:div
   {:class ["w-3 h-3 rounded-full bg-ui-red absolute -top-1 left-4 border-solid border-2 border-ui-white"
            "group-hover:border-ui-blue-10 group-focus:border-ui-blue-10"]}])


(defn- active-indicator [active?]
  [:div.absolute.bottom-0.left-0.w-full.bg-ui-violet-100.transition-all
   {:class [(if active? "h-0.5" "h-0")]}])


(def ^:private NovaCloud24
  "React component wrapper around `i/novacloud` so it can be passed wherever
  Carbon icon components like `Task24` are expected."
  (r/reactify-component
    (fn []
      [cc/icon {:icon        i/novacloud
                :description ""
                :class-name  "w-6 h-6"
                :style       {:stroke "currentColor" :stroke-width "1.25"}}])))


(defn- icon-wrapper [icon has-notification?]
  [:div.animate-appear-pop-out.relative.flex.items-center.justify-center
   [:> icon]
   (when has-notification? [notification-indicator])])

(defn- label-with-count
  [label count]
  (cond-> label
    (pos? count) (str " (" count ")")))


(defn- button [{:keys [text
                       text-subtitle
                       icon
                       href
                       on-click
                       active?
                       has-notification?
                       hide-on-mobile?
                       mobile-menu-button?
                       class
                       *ref
                       aria-controls
                       aria-expanded
                       hide-text?]}]
  [(if href :a :button)
   (p/assoc-if {:class      ["items-center justify-start px-2 lg:px-4 gap-2 rounded-none relative group border-0"
                             "bg-ui-white can-hover:hover:bg-ui-blue-10 appearance-none font-semibold text-ui-night-sky-100"
                             "focus:z-10 focus:bg-ui-blue-10 no-underline group-[.mobile-menu]:h-12 text-xs"
                             "group-[.mobile-menu]:px-6 group-[.mobile-menu]:font-normal group-[.mobile-menu]:text-base"
                             (if hide-on-mobile?
                               "hidden lg:flex group-[.mobile-menu]:flex"
                               "flex")
                             class]
                :aria-label text}
               :href href
               :on-click on-click
               :ref (when *ref #(reset! *ref %))
               :aria-controls aria-controls
               :aria-expanded aria-expanded)
   (if active?
     ^{:key "close-icon"} [icon-wrapper Close24 false]
     (when icon
       ^{:key "item-icon"} [icon-wrapper icon has-notification?]))
   (when-not hide-text?
     [:span
      {:class (when-not mobile-menu-button? "hidden lg:block group-[.mobile-menu]:block text-left")}
      text
      (when text-subtitle
        [:<> [:br] [:span {:class "text-xs font-normal"} text-subtitle]])])
   [active-indicator (and active? (not mobile-menu-button?))]])


(defn- primary-action-wrapper [& children]
  (into [:div.flex.items-center.bg-ui-white.py-1
         {:class ["[&>div]:!gap-1 [&>div]:!h-full [&_button]:!h-full [&_button]:!mx-1 ml-px lg:ml-0"
                  "[&_.bx--inline-loading]:mx-4  [&_.bx--inline-loading]:mt-1"]}] ; Fix workspace primary action loading indicator whitespace
        children))


(defn- mobile-menu-button [{:keys [tr *ref active? on-click]}]
  [button {:text                (tr [(if active? :close-menu :menu)])
           :icon                Menu24
           :active?             active?
           :has-notification?   false
           :class               "flex lg:hidden"
           :mobile-menu-button? true
           :*ref                *ref
           :on-click            on-click
           :aria-controls       "mobile-menu"
           :aria-expanded       (str active?)}])


(defn- mobile-menu-wrapper [{:keys [*trigger-button-ref]} & _]
  (let [*top             (r/atom 0)
        *shrink-teardown (atom nil)
        update-top       (fn []
                           (reset! *top (or (some-> @*trigger-button-ref .getBoundingClientRect .-bottom) 0)))]
    (r/create-class
      {:component-did-update
       update-top
       :component-did-mount
       (fn []
         (.addEventListener js/window "scroll" update-top #js {:passive true})
         (.addEventListener js/window "resize" update-top)
         (reset! *shrink-teardown
                 (visual-viewport/add-shrink-listener
                   (fn []
                     ;; When viewport shrinks (keyboard opened) and the mobile menu
                     ;; is open, scroll the header into view so the panel stays
                     ;; visible above the keyboard.
                     (when @*trigger-button-ref
                       (when-let [menu-el (.getElementById js/document "mobile-menu")]
                         (when (pos? (.-offsetHeight menu-el))
                           (.requestAnimationFrame js/window
                                                   (fn []
                                                     (.scrollIntoView @*trigger-button-ref #js {:block "start" :behavior "instant"})
                                                     (update-top)))))))))
         (update-top))
       :component-will-unmount
       (fn []
         (.removeEventListener js/window "scroll" update-top)
         (.removeEventListener js/window "resize" update-top)
         (when-let [teardown @*shrink-teardown]
           (teardown)))
       :reagent-render
       (fn [{:keys [open?]} & children]
         (let [viewport-h (<sub [::visual-viewport/height])
               content-h  (max 0 (- viewport-h @*top))
               full-h     (max 0 (- (.-innerHeight js/window) @*top))]
           [:div#mobile-menu.absolute.top-full.-mt-px.w-full.bg-ui-white.overflow-y-hidden.lg:hidden
            (merge {:class (when open? "animate-expand-height")
                    :style {:height (if open? (str (max content-h full-h) "px") "0px")}}
                   (when-not open?
                     {:inert ""}))
            (into [:div.w-full.overflow-hidden
                   {:style {:height (if open? (str content-h "px") "0px")}}]
                  children)]))})))


(defn- mobile-menu-subheader [text icon]
  [:h2.mx-4.p-2.font-normal.text-base.flex.flex-row.gap-2.items-center
   (when icon [icon-wrapper icon false])
   text])


(defn- mobile-menu [tr main-menu-items]
  (let [open-panel (<sub [::panels/open-panel])
        panel-kind (when (vector? open-panel) (first open-panel))]
    [:div.flex.flex-col.group.mobile-menu.w-full.h-full.items-stretch.overflow-x-hidden
     [:div.flex.flex-row.h-full.transition-transform
      {:style {:width     "200%"
               :transform (if open-panel "translateX(-50%)" "translateX(0%)")}}
      [:div.flex.flex-1.flex-col.overflow-x-hidden
       main-menu-items]
      [:div.flex.flex-1.overflow-x-hidden
       [:div.flex.flex-1.flex-col.items-stretch
        [:div.flex.flex-row.border-b-2.border-solid.border-ui-violet-100
         [button {:text       (tr [:back])
                  :icon       ChevronLeft24
                  :on-click   #(>evt [::panels/close-all true])
                  :hide-text? true}]
         [:div.h-8.bg-ui-gray.self-center {:class ["w-px"]}]
         (when (and open-panel (not (#{:contact-request :cta :chat} panel-kind)))
           (let [tr-key (keyword :navigation panel-kind)
                 title  (tr [tr-key])
                 icon   (get menu-item-icon-mapping tr-key)]
             [mobile-menu-subheader title icon]))]
        ;; Target for (portal/render-in-portal) from app.front.ui.side-panel:
        [:div#mobile-side-panel-content.flex.flex-1.flex-col.items-stretch.p-4.min-h-0]]]]]))


(defn sub-header [tr match]
  (r/with-let [*mobile-menu-button-ref (r/atom nil)]
    (let [public?                   (-> match :data :public?)
          route-name                (-> match :data :name)
          full-screen-view?         (-> match :data :full-screen-view?)
          primary-actions-component (-> match :data :primary-actions)
          show-back-button?         (not (#{:dashboard :view/landing-page} route-name))
          view-sub                  (some-> match :data :view-sub)
          {:keys [back-href
                  primary-action
                  options
                  alerts]}          (cond-> (<sub [::default-view-data])
                                      (some? view-sub)
                                      (merge (<sub view-sub)))
          has-options?              (and view-sub (seq (:items options)))
          panel-open?               (<sub [::options-panel/panel-open?])
          mobile-menu-open?         (<sub [::panels/mobile-menu-open?])
          has-tasks?                (<sub [::tasks-panel/has-items?])
          tasks-open?               (<sub [::tasks-panel/panel-open?])
          tasks-count               (<sub [::tasks-panel/task-count])
          calls-to-action           (<sub [::landing-page-subs/calls-to-action])
          open-cta-index            (<sub [::call-to-action/open-panel])
          chat-open?                (<sub [::chat-panel/panel-open?])
          chat-enabled?             (<sub [::chat-panel/chat-enabled?])
          service-hub-open?         (<sub [::service-hub-panel/panel-open?])
          messages-open?            (<sub [::messages-panel/panel-open?])
          unread-count              (<sub [::messages-panel/unread-count])
          current-ws-id             (-> match :parameters :path :workspace-id)
          lang                      (<sub [:language])
          logged-in?                (<sub [:session/logged-in?])
          main-menu-items           [:<>
                                     (when chat-enabled?
                                       [button {:text              (tr [:chat/heading])
                                                :icon              NovaCloud24
                                                :active?           chat-open?
                                                :on-click          #(>evt [::chat-panel/toggle-open {:open? (not chat-open?)}])
                                                :has-notification? false
                                                :hide-on-mobile?   true}])
                                     (when (and logged-in?
                                                (ff/enabled? :feature-flag.id/service-hub-panel))
                                       [button {:text              (tr [:navigation/service-hub])
                                                :icon              (:navigation/service-hub menu-item-icon-mapping)
                                                :active?           service-hub-open?
                                                :on-click          #(>evt [::service-hub-panel/toggle-open])
                                                :has-notification? false
                                                :hide-on-mobile?   true}])
                                     (when public?
                                       (for-frag [[cta-index call-to-action] (map-indexed vector calls-to-action)]
                                         [button {:key               (str "cta-button-" cta-index)
                                                  :text              (call-to-action/call-to-action-title call-to-action lang)
                                                  :text-subtitle     (call-to-action/call-to-action-subtitle call-to-action lang)
                                                  :icon              (get carbon-icons/icons (:call-to-action/icon call-to-action) Information24)
                                                  :active?           (= cta-index open-cta-index)
                                                  :on-click          #(>evt [::call-to-action/toggle-open cta-index])
                                                  :has-notification? false
                                                  :hide-on-mobile?   true}]))
                                     (when has-tasks?
                                       [button {:text              (label-with-count (tr [:navigation/tasks]) tasks-count)
                                                :icon              Task24
                                                :active?           tasks-open?
                                                :on-click          #(>evt [::tasks-panel/toggle-open])
                                                :has-notification? has-tasks?
                                                :hide-on-mobile?   true}])
                                     (when (and logged-in? (not= route-name :view/messages))
                                       [button {:text              (label-with-count (tr [:navigation/messages]) unread-count)
                                                :icon              (:navigation/messages menu-item-icon-mapping)
                                                :active?           messages-open?
                                                :on-click          #(>evt [::messages-panel/toggle-open current-ws-id])
                                                :has-notification? (pos? unread-count)
                                                :hide-on-mobile?   true}])
                                     (when has-options?
                                       [button {:text              (tr [:navigation/options])
                                                :icon              (:navigation/options menu-item-icon-mapping)
                                                :active?           panel-open?
                                                :on-click          #(>evt [::options-panel/toggle-open])
                                                :has-notification? false
                                                :hide-on-mobile?   true}])]
          mobile-shortcut-items     [:<>
                                     (when has-tasks?
                                       [button {:text              (label-with-count (tr [:navigation/tasks]) tasks-count)
                                                :icon              Task24
                                                :on-click          #(do
                                                                      (>evt [::panels/toggle-mobile-menu-open true])
                                                                      (>evt [::tasks-panel/toggle-open]))
                                                :has-notification? has-tasks?}])
                                     (when (and logged-in? (not= route-name :view/messages))
                                       [button {:text              (label-with-count (tr [:navigation/messages]) unread-count)
                                                :icon              (:navigation/messages menu-item-icon-mapping)
                                                :on-click          #(do
                                                                      (>evt [::panels/toggle-mobile-menu-open true])
                                                                      (>evt [::messages-panel/toggle-open current-ws-id]))
                                                :has-notification? (pos? unread-count)}])]]
      [:<>
       [:div#root-sub-header.bg-ui-gray.h-12.shadow-lg.border-b.border-solid.border-ui-gray.flex.flex-row.items-stretch.lg:gap-px
        (when-not full-screen-view?
          [mobile-menu-button {:tr       tr
                               :*ref     *mobile-menu-button-ref
                               :active?  mobile-menu-open?
                               :on-click #(do
                                            (-> % .-currentTarget .blur)
                                            (>evt [::panels/toggle-mobile-menu-open]))}])
        (when show-back-button?
          [button {:text              (tr [:back])
                   :icon              ChevronLeft24
                   :href              back-href
                   :active?           false
                   :hide-on-mobile?   (not full-screen-view?)
                   :has-notification? false}])
        [:div.hidden.lg:flex.flex-1.items-center.px-2.bg-ui-white
         [nav/breadcrumb (util/mmap {:class-name "pl-2"}
                                    tr match)]]
        [:div.flex-1.lg:hidden.bg-ui-white]
        (when-not full-screen-view?
          [:div.flex-row.items-stretch.lg:gap-px
           {:class (if mobile-menu-open? "hidden lg:flex" "flex")}
           main-menu-items
           [:div.flex.flex-row.lg:hidden mobile-shortcut-items]
           (when-let [{:keys [evt tr-key disabled?]} primary-action]
             [primary-action-wrapper
              [ui/button
               (merge {:size     :field
                       :disabled (boolean disabled?)}
                      (when evt
                        {:on-click #(>evt evt)}))
               (tr [tr-key])]])
           (when primary-actions-component
             [primary-action-wrapper
              [primary-actions-component match]])])
        (when-not full-screen-view?
          [mobile-menu-wrapper
           {:*trigger-button-ref *mobile-menu-button-ref
            :open?               mobile-menu-open?}
           [mobile-menu tr main-menu-items]])]
       (when-not mobile-menu-open?
         (for-frag [[severity text] (remove nil? alerts)]
           [top-bar/alert-banner {:severity severity}
            text]))
       [primary-actions/confirmation-modal]])))
